import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import api from '../api/client';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useNotice } from '../components/common/Notices';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useApps } from '../contexts/AppsContext';
import { encryptPassword } from '../utils/crypto';
import { getCurrentUser } from '../utils/storage';
import { cn } from '../utils/cn';

// Two-step verification for the signed-in person: an authenticator app (TOTP) code at
// sign-in, on top of the password. Available only when the session's app allows it.
// Rendered in the browser: the otpauth URI carries the TOTP secret, so it must never be
// sent to a third-party QR service.
const LocalQrCode = ({ value }) => {
    const [src, setSrc] = useState('');
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        QRCode.toDataURL(value, { width: 200, margin: 1, errorCorrectionLevel: 'M' })
            .then((url) => !cancelled && setSrc(url))
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
        };
    }, [value]);
    if (failed) {
        return <p className="text-sm text-slate-600">Couldn’t draw the QR code. Type the setup key below instead.</p>;
    }
    if (!src) return <div className="h-[200px] w-[200px] animate-pulse rounded-lg bg-slate-100" aria-hidden="true" />;
    return (
        <img
            src={src}
            alt="QR code for your authenticator app"
            className="rounded-lg border border-slate-200"
            width={200}
            height={200}
        />
    );
};

const TotpSetupPage = () => {
    const notice = useNotice();
    const { apps } = useApps();
    const currentUser = getCurrentUser();
    const sessionApp = currentUser?.appId ? apps.find((a) => a.app_id === currentUser.appId) : null;
    const sessionAppName = sessionApp?.app_name || '';

    const [status, setStatus] = useState({ totp_enabled: false, totp_mfa_enabled: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Setup state
    const [setupData, setSetupData] = useState(null); // { secret_b32, otpauth_uri, setup_token }
    const [verifyCode, setVerifyCode] = useState('');
    const [setupLoading, setSetupLoading] = useState(false);

    // Disable state
    const [showDisable, setShowDisable] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [disableLoading, setDisableLoading] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/auth/totp/status');
            setStatus(res?.data?.data || { totp_enabled: false, totp_mfa_enabled: false });
        } catch (err) {
            setError(
                err?.response?.data?.message || 'Couldn’t check your two-step verification status. Reload to try again.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleSetup = async () => {
        setSetupLoading(true);
        setError('');
        try {
            const res = await api.post('/api/auth/totp/setup');
            setSetupData(res?.data?.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Couldn’t start setup. Try again in a moment.');
        } finally {
            setSetupLoading(false);
        }
    };

    const handleVerifySetup = async (e) => {
        e.preventDefault();
        setSetupLoading(true);
        setError('');
        try {
            await api.post('/api/auth/totp/verify-setup', {
                setup_token: setupData.setup_token,
                totp_code: verifyCode,
            });
            notice.success('Two-step verification is on. You’ll be asked for a code when you sign in.');
            setSetupData(null);
            setVerifyCode('');
            fetchStatus();
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    'That code didn’t work. Check the time on your phone is correct and enter the newest code.'
            );
        } finally {
            setSetupLoading(false);
        }
    };

    const handleDisable = async (e) => {
        e.preventDefault();
        setDisableLoading(true);
        setError('');
        try {
            await api.post('/api/auth/totp/disable', { password: encryptPassword(disablePassword) });
            notice.success('Two-step verification is off.');
            setShowDisable(false);
            setDisablePassword('');
            fetchStatus();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Couldn’t turn off two-step verification.');
        } finally {
            setDisableLoading(false);
        }
    };

    if (loading) {
        return <SkeletonList rows={1} />;
    }

    return (
        <div className="max-w-3xl space-y-6">
            <PageHeader
                title="Two-step verification"
                subtitle="Ask for a 6-digit code from an authenticator app (TOTP) each time you sign in, as well as your password."
            />

            {!status.totp_mfa_enabled && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p>
                        Two-step verification with an authenticator app isn’t turned on for{' '}
                        {sessionAppName ? <span className="font-semibold">{sessionAppName}</span> : 'this app'}, so you
                        can’t set it up yet.{' '}
                        {currentUser?.isPortalAdmin
                            ? 'You can turn it on in the app’s settings.'
                            : 'A portal admin can turn it on in the app’s settings.'}
                    </p>
                    {currentUser?.isPortalAdmin && (
                        <Link
                            to={`/apps${sessionAppName ? `?search=${encodeURIComponent(sessionAppName)}` : ''}`}
                            className="mt-2 inline-flex min-h-11 items-center font-medium text-amber-900 underline underline-offset-4 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 md:min-h-0"
                        >
                            Turn it on in app settings
                        </Link>
                    )}
                </div>
            )}

            {error && (
                <div role="alert" className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Status */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div
                            className={cn(
                                'grid h-10 w-10 place-content-center rounded-lg',
                                status.totp_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                            )}
                        >
                            <Smartphone className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-slate-900">Authenticator app</h2>
                            <p
                                className={cn(
                                    'text-sm',
                                    status.totp_enabled ? 'font-medium text-emerald-700' : 'text-slate-600'
                                )}
                            >
                                {status.totp_enabled ? 'On — a code is required at sign-in' : 'Off'}
                            </p>
                        </div>
                    </div>

                    {status.totp_mfa_enabled && !status.totp_enabled && !setupData && (
                        <Button onClick={handleSetup} disabled={setupLoading}>
                            {setupLoading ? 'Starting setup…' : 'Set up authenticator app'}
                        </Button>
                    )}

                    {status.totp_enabled && !showDisable && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowDisable(true);
                                setError('');
                            }}
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 focus-visible:ring-red-200"
                        >
                            Turn off
                        </Button>
                    )}
                </div>
            </div>

            {/* Setup Flow — QR Code + Verification */}
            {setupData && (
                <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 md:p-6">
                    <div>
                        <h2 className="text-base font-semibold text-slate-900">Scan the QR code</h2>
                        <p className="mt-1 text-sm text-slate-600">
                            Open your authenticator app (for example Google Authenticator, Microsoft Authenticator or
                            1Password) and scan this code, or type the key below into the app.
                        </p>
                    </div>

                    <div className="flex justify-center">
                        <LocalQrCode value={setupData.otpauth_uri} />
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="mb-1 text-xs text-slate-600">Setup key (if you can’t scan)</div>
                        <code className="select-all break-all font-mono text-sm text-slate-900">
                            {setupData.secret_b32}
                        </code>
                    </div>

                    <form onSubmit={handleVerifySetup} className="space-y-3">
                        <label htmlFor="totp-verify-code" className="block text-sm font-medium text-slate-700">
                            Enter the 6-digit code the app shows
                        </label>
                        <Input
                            id="totp-verify-code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            required
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="h-11 max-w-xs text-center text-lg tracking-widest"
                            maxLength={6}
                        />
                        <div className="flex flex-wrap gap-3">
                            <Button type="submit" disabled={setupLoading || verifyCode.length !== 6}>
                                {setupLoading ? 'Checking code…' : 'Turn on'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setSetupData(null);
                                    setVerifyCode('');
                                    setError('');
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            )}

            {/* Disable Confirmation */}
            {showDisable && (
                <div className="rounded-xl border border-red-200 bg-white p-5 md:p-6">
                    <h2 className="text-base font-semibold text-slate-900">Turn off two-step verification?</h2>
                    <p className="mt-1 text-sm text-slate-600">
                        You’ll sign in with just your password. To use an authenticator app again you’ll need to scan a
                        new code.
                    </p>
                    <form onSubmit={handleDisable} className="mt-4 space-y-3">
                        <label htmlFor="totp-disable-password" className="block text-sm font-medium text-slate-700">
                            Your password
                        </label>
                        <Input
                            id="totp-disable-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            className="max-w-sm"
                        />
                        <div className="flex flex-wrap gap-3">
                            <Button type="submit" variant="destructive" disabled={disableLoading || !disablePassword}>
                                {disableLoading ? 'Turning off…' : 'Turn off'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowDisable(false);
                                    setDisablePassword('');
                                    setError('');
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default TotpSetupPage;
